import store from 'app/store';
import {
  AttributeCodeType,
  AttributeType,
  TruncatedAttributeType
} from 'data/types';
import { coerceCheckboxValue } from 'utils/checkbox-value';
import { isNotEmpty } from 'utils/helpers';
import { errorModal } from 'utils/ui';

export function filterAttributes(
  attributes: AttributeType[],
  type: 'normal' | 'advanced' | 'both' = 'both',
  group?: boolean | string
) {
  return attributes.filter(
    (a) =>
      (typeof group === 'string' ? a.group === group : true) &&
      (typeof group === 'boolean' ? !!a.group === group : true) &&
      // eslint-disable-next-line no-nested-ternary
      (type === 'normal'
        ? !a.advanced
        : type === 'advanced'
        ? a.advanced
        : true) &&
      a.code !== 'auto_layout_space' &&
      (!a.parentAttributes ||
        a.parentAttributes.some(
          (pa) =>
            pa.value?.toString() ===
            attributes.find((na) => na.id === pa.attributeId)?.value?.toString()
        ) ||
        a.parentAttributes.every(
          (pa) => !attributes.find((na) => na.id === pa.attributeId)
        ))
  );
}

export function sortAttributes(attributes: AttributeType[]) {
  const sortedAttributes = [...attributes]
    .sort((a, b) => a.label.localeCompare(b.label))
    .sort((a, b) => a.priority - b.priority)
    .sort((a, b) => (a.group || 'zzz').localeCompare(b.group || 'zzz'))
    .sort(
      (a, b) =>
        (b.type === 'checkbox' ? 0 : 1) - (a.type === 'checkbox' ? 0 : 1)
    );

  attributes.forEach((att) => {
    if (att.parentAttributes) {
      const lastValidParentAttribute = [...sortedAttributes]
        .reverse()
        .find((a) =>
          att.parentAttributes?.some(
            (pa) => pa.attributeId === a.id && pa.value === a.value
          )
        );
      if (lastValidParentAttribute) {
        const fromIndex = sortedAttributes.findIndex((a) => a.id === att.id);
        const toIndex = sortedAttributes.findIndex(
          (a) => a.id === lastValidParentAttribute.id
        );
        if (fromIndex !== -1 && toIndex !== -1) {
          const splicedAtt = sortedAttributes.splice(fromIndex, 1)[0];
          sortedAttributes.splice(
            fromIndex > toIndex ? toIndex + 1 : toIndex,
            0,
            splicedAtt
          );
        }
      }
    }
  });

  return sortedAttributes;
}

export function allAttributesCompleted(attributes: AttributeType[]) {
  return filterAttributes(attributes).every((att) => isNotEmpty(att.value));
}

export function allRequiredAttributesCompleted(attributes: AttributeType[]) {
  return filterAttributes(attributes).every(
    (att) => !att.required || isNotEmpty(att.value)
  );
}

export function getPercentageOfRequiredAttributesCompleted(
  attributes: AttributeType[]
) {
  const required = filterAttributes(attributes).filter(
    (att) => att.required
  ).length;
  const completed = filterAttributes(attributes).filter(
    (att) => !att.required || isNotEmpty(att.value)
  ).length;

  return (completed / required) * 100;
}

export function getAttributeValue(
  attributes: AttributeType[],
  code: AttributeCodeType
) {
  const attribute = attributes.find((a) => a.code === code);

  return attribute?.type === 'number'
    ? Number(attribute.value)
    : attribute?.value;
}

// Checkbox attribute values round-trip through the database as varchar
// 'true'/'false' strings; 'false' is truthy, so consumers must coerce.
export function getBooleanAttributeValue(
  attributes: AttributeType[],
  code: AttributeCodeType
): boolean {
  const value = attributes.find((a) => a.code === code)?.value;
  return coerceCheckboxValue(value);
}

export function truncateAttributes(originalAttributes: AttributeType[]) {
  return originalAttributes.map((att) => ({
    code: att.code,
    value: att.value
  })) as TruncatedAttributeType[];
}

export function restoreAttributes(
  truncatedAttributes: TruncatedAttributeType[]
) {
  const { attributes } = store.getState().data;

  return truncatedAttributes.map((att) => {
    const originalAttribute = attributes.find((a) => a.code === att.code);

    if (!originalAttribute) {
      console.error(`Attribute '${att.code}' not found`);
      errorModal(`Attribute '${att.code}' not found`);
      return att;
    }

    return {
      ...originalAttribute,
      value: att.value
    };
  }) as AttributeType[];
}

export function convertBooleanValueToYesNo(value: boolean | undefined) {
  // eslint-disable-next-line no-nested-ternary
  return value === true ? 'Yes' : value === false ? 'No' : undefined;
}

export function convertYesNoValueToBoolean(value: string | undefined) {
  // eslint-disable-next-line no-nested-ternary
  return value === 'Yes' ? true : value === 'No' ? false : undefined;
}
